import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, Outlet, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "./context/AuthContext";
import { Loading } from "./components/common";
import AppShell, { ComingSoon } from "./components/AppShell";
import Login from "./pages/Login";
import FirstAccess from "./pages/FirstAccess";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FinancialHold from "./pages/FinancialHold";
import FinanceCenter from "./pages/backoffice/FinanceCenter";
import FinanceClient from "./pages/backoffice/FinanceClient";
import BoReferrals from "./pages/backoffice/BoReferrals";
import Dashboard from "./pages/company/Dashboard";
import Employees from "./pages/company/Employees";
import DemoLanding from "./pages/public/DemoLanding";
import EmployeeDetail from "./pages/company/EmployeeDetail";
import Requests from "./pages/company/Requests";
import Admissions from "./pages/company/Admissions";
import Payroll from "./pages/company/Payroll";
import FolhaHome from "./pages/company/FolhaHome";
import MonthlyMovements from "./pages/company/MonthlyMovements";
import Dismissals from "./pages/company/Dismissals";
import Vacations from "./pages/company/Vacations";
import Benefits from "./pages/company/Benefits";
import Documents from "./pages/company/Documents";
import Indicators from "./pages/company/Indicators";
import SettingsPage from "./pages/company/Settings";
import BoOverview from "./pages/backoffice/Overview";
import Queue from "./pages/backoffice/Queue";
import Clients from "./pages/backoffice/Clients";
import BoArchived from "./pages/backoffice/BoArchived";
import BoFolhas from "./pages/backoffice/BoFolhas";
import BoEmailQueue from "./pages/backoffice/BoEmailQueue";
import ClientDetail from "./pages/backoffice/ClientDetail";
import BoCompetencias from "./pages/backoffice/Competencias";
import EmployeePortal from "./pages/employee/EmployeePortal";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPermissions from "./pages/admin/AdminPermissions";
import AdmissionForm from "./pages/public/AdmissionForm";
import AdminIntegrations from "./pages/admin/AdminIntegrations";
import FileCenterPage from "./pages/company/FileCenter";
import AdminFileCategories from "./pages/admin/AdminFileCategories";
import BoFiles from "./pages/backoffice/BoFiles";
import BoMovements from "./pages/backoffice/BoMovements";
import BoClosings from "./pages/backoffice/BoClosings";
import OpsCompanies from "./pages/backoffice/OpsCompanies";
import CalendarPage from "./pages/backoffice/Calendar";
import OpsCenter from "./pages/backoffice/OpsCenter";
import SignContract from "./pages/public/SignContract";
import AdminContractTemplates from "./pages/admin/AdminContractTemplates";
import ClientHome from "./pages/admin/ClientHome";
import AdminPortfolio from "./pages/admin/AdminPortfolio";
import AdminLanding from "./pages/admin/AdminLanding";
import BoLeads from "./pages/backoffice/BoLeads";
import Landing from "./pages/public/Landing";

function AccessDenied() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="access-denied">
      <div className="max-w-md text-center bg-white border rounded-2xl p-8">
        <ShieldAlert className="h-10 w-10 text-rose-600 mx-auto" />
        <h1 className="text-2xl font-bold mt-4">Acesso negado</h1>
        <p className="text-sm text-slate-600 mt-2">Você está conectado como <b>{user.name}</b> ({user.role_label}). Este perfil não tem acesso a esta área.</p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center mt-6">
          <Button asChild data-testid="access-denied-home"><Link to={homeFor(user)}>Voltar para minha área</Link></Button>
          <Button asChild variant="outline" data-testid="access-denied-login"><Link to="/login">Entrar com outra conta</Link></Button>
        </div>
      </div>
    </div>
  );
}

function FirstAccessGate() {
  const { user } = useAuth();
  if (new URLSearchParams(window.location.search).get("token")) return <FirstAccess />;
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return user.must_change_password ? <FirstAccess /> : <Navigate to={homeFor(user)} replace />;
}

function Guard({ perm, children }) {
  const { user, can } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/primeiro-acesso" replace />;
  if (perm && !can(perm)) return <AccessDenied />;
  return children;
}

function LegacyAdmin() {
  const { pathname, search } = useLocation();
  return <Navigate to={`/backoffice${pathname}${search}`} replace />;
}

function Root() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/demonstracao" element={<DemoLanding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/esqueci-senha" element={<ForgotPassword />} />
          <Route path="/redefinir-senha" element={<ResetPassword />} />
          <Route path="/pendencia-financeira" element={<FinancialHold />} />
          <Route path="/primeiro-acesso" element={<FirstAccessGate />} />
          <Route path="/admissao/:token" element={<AdmissionForm />} />
          <Route path="/assinatura/:token" element={<SignContract />} />
          <Route path="/empresa" element={<Guard perm="dashboard.view"><AppShell env="empresa" /></Guard>}>
            <Route index element={<Dashboard />} />
            <Route path="funcionarios" element={<Employees />} />
            <Route path="funcionarios/mensal" element={<MonthlyMovements />} />
            <Route path="desligamentos" element={<Dismissals />} />
            <Route path="funcionarios/:id" element={<EmployeeDetail />} />
            <Route path="admissoes" element={<Admissions />} />
            <Route path="movimentacoes" element={<Requests movements />} />
            <Route path="solicitacoes" element={<Requests />} />
            <Route path="enviar-folha" element={<Navigate to="/empresa/folha" replace />} />
            <Route path="folha" element={<FolhaHome />} />
            <Route path="folha/categorias" element={<Payroll />} />
            <Route path="ferias" element={<Vacations />} />
            <Route path="beneficios" element={<Benefits />} />
            <Route path="documentos" element={<Documents />} />
            <Route path="arquivos" element={<FileCenterPage />} />
            <Route path="indicadores" element={<Indicators />} />
            <Route path="configuracoes" element={<SettingsPage />} />
            <Route path="recrutamento" element={<ComingSoon title="Recrutamento e banco de currículos" />} />
            <Route path="relatorios" element={<ComingSoon title="Relatórios (Excel, CSV, PDF)" />} />
          </Route>
          <Route path="/backoffice" element={<Guard perm="backoffice.access"><AppShell env="backoffice" /></Guard>}>
            <Route index element={<OpsCenter />} />
            <Route path="operacional/:cid" element={<ClientHome backoffice />} />
            <Route path="visao-geral" element={<BoOverview />} />
            <Route path="fila" element={<Queue />} />
            <Route path="pendencias" element={<Queue pendencias />} />
            <Route path="clientes" element={<Clients />} />
            <Route path="arquivadas" element={<Guard perm="admin.access"><BoArchived /></Guard>} />
            <Route path="folhas" element={<BoFolhas />} />
            <Route path="clientes/:id" element={<ClientDetail />} />
            <Route path="competencias" element={<BoCompetencias />} />
            <Route path="arquivos" element={<BoFiles />} />
            <Route path="movimentacoes" element={<BoMovements />} />
            <Route path="fechamentos" element={<BoClosings />} />
            <Route path="central" element={<OpsCompanies />} />
            <Route path="calendario" element={<CalendarPage />} />
            <Route path="leads" element={<Guard perm="leads.manage"><BoLeads /></Guard>} />
            <Route path="indicacoes" element={<Guard perm="leads.manage"><BoReferrals /></Guard>} />
            <Route path="financeiro" element={<Guard perm="finance.manage"><FinanceCenter /></Guard>} />
            <Route path="financeiro/:cid" element={<Guard perm="finance.manage"><FinanceClient /></Guard>} />
            <Route path="admin" element={<Guard perm="admin.access"><Outlet /></Guard>}>
            <Route index element={<AdminOverview />} />
            <Route path="empresas" element={<AdminCompanies />} />
            <Route path="usuarios" element={<AdminUsers />} />
            <Route path="emails" element={<BoEmailQueue />} />
            <Route path="permissoes" element={<AdminPermissions />} />
            <Route path="integracoes" element={<AdminIntegrations />} />
            <Route path="categorias-arquivos" element={<AdminFileCategories />} />
            <Route path="modelos-contrato" element={<AdminContractTemplates />} />
            <Route path="clientes/:cid" element={<ClientHome />} />
            <Route path="carteira" element={<AdminPortfolio />} />
            <Route path="landing" element={<AdminLanding />} />
            </Route>
          </Route>
          <Route path="/admin/*" element={<LegacyAdmin />} />
          <Route path="/funcionario" element={<Guard perm="self.view"><EmployeePortal /></Guard>} />
          <Route path="*" element={<Root />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
